import { useState } from 'react'
import {
  Box,
  ButtonBase,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import {
  Close as CloseIcon,
  Menu as MenuIcon,
  SpaceDashboard as DashboardIcon,
  BugReport as VulnerabilitiesIcon,
  SystemUpdateAlt as UpgradesIcon,
  Business as ClientOrgIcon
} from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { brand, brandNavItems } from '../config'
import { resolveActiveBoard, resolveBoardAccent } from './SocrBoardStatusBar'

const isBoardActive = (pathname, board) => {
  if (!board?.path) return false
  if (board.path === '/') return pathname === '/' || pathname === ''
  return pathname === board.path || pathname.startsWith(`${board.path}/`)
}

const BOARD_ICONS = {
  dashboard: DashboardIcon,
  vulnerabilities: VulnerabilitiesIcon,
  upgrades: UpgradesIcon,
  clientOrganization: ClientOrgIcon
}

/**
 * Logo + SOCR mark opens a side drawer listing product boards.
 * Dark menu, but lifted mid-tones — not near-black.
 */
const SocrNavDrawer = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  const activeBoard = resolveActiveBoard(pathname)
  const shellAccent = resolveBoardAccent(theme, activeBoard.id)

  const panelBg = '#3a3f46'
  const panelSurface = '#454b54'
  const panelInk = alpha('#fff', 0.94)
  const panelMuted = alpha('#fff', 0.62)

  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-label='Open SOCR navigation'
        aria-expanded={open ? 'true' : 'false'}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          height: { xs: 42, sm: 44 },
          px: { xs: 1, sm: 1.15 },
          borderRadius: 1.5,
          border: `1px solid ${alpha(shellAccent, open ? 0.55 : 0.32)}`,
          backgroundColor: open ? alpha(shellAccent, 0.28) : alpha('#000', 0.34),
          backgroundImage: open
            ? `linear-gradient(135deg, ${alpha(shellAccent, 0.35)} 0%, ${alpha(shellAccent, 0.12)} 100%)`
            : `linear-gradient(180deg, ${alpha('#fff', 0.07)} 0%, transparent 100%)`,
          boxShadow: open
            ? `inset 0 0 0 1px ${alpha(shellAccent, 0.25)}, 0 4px 12px ${alpha(shellAccent, 0.2)}`
            : `inset 0 1px 0 ${alpha('#fff', 0.07)}`,
          transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
          '&:hover': {
            borderColor: alpha(shellAccent, 0.55),
            backgroundColor: alpha(shellAccent, 0.24),
            backgroundImage: `linear-gradient(135deg, ${alpha(shellAccent, 0.3)} 0%, ${alpha(shellAccent, 0.1)} 100%)`
          }
        }}
      >
        <Box
          component='img'
          alt=''
          src='/keysys_logo_white.png'
          sx={{
            height: { xs: 26, sm: 28 },
            width: 'auto',
            display: 'block',
            flexShrink: 0,
            opacity: 0.98
          }}
        />
        <Typography
          component='span'
          sx={{
            fontWeight: 900,
            fontSize: { xs: '0.88rem', sm: '0.95rem' },
            letterSpacing: 2,
            lineHeight: 1,
            color: alpha(theme.palette.common.white, 0.96),
            whiteSpace: 'nowrap'
          }}
        >
          {brand.name}
        </Typography>
        <MenuIcon
          sx={{
            fontSize: { xs: 20, sm: 22 },
            ml: 0.15,
            opacity: 0.85,
            color: alpha('#fff', 0.9)
          }}
        />
      </ButtonBase>

      <Drawer
        anchor='left'
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: alpha('#000', 0.35) }
          },
          paper: {
            sx: {
              width: { xs: 'min(340px, 92vw)', sm: 308 },
              color: panelInk,
              borderRight: `1px solid ${alpha(shellAccent, 0.22)}`,
              backgroundColor: panelBg,
              backgroundImage: `linear-gradient(195deg, ${alpha(shellAccent, 0.2)} 0%, ${alpha(shellAccent, 0.06)} 36%, transparent 62%)`,
              boxShadow: `8px 0 36px ${alpha('#000', 0.28)}`
            }
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            px: 2.15,
            pt: 2,
            pb: 1.65,
            background: `linear-gradient(90deg, ${alpha(shellAccent, 0.2)} 0%, ${alpha(panelSurface, 0.9)} 100%)`,
            borderBottom: `1px solid ${alpha('#fff', 0.1)}`,
            boxShadow: `inset 3px 0 0 ${shellAccent}`
          }}
        >
          <IconButton
            aria-label='Close navigation'
            onClick={() => setOpen(false)}
            size='small'
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: panelMuted,
              backgroundColor: alpha('#fff', 0.06),
              '&:hover': { backgroundColor: alpha('#fff', 0.12), color: panelInk }
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.95, pr: 4 }}>
            <Box
              component='img'
              alt=''
              src='/keysys_logo_white.png'
              sx={{ height: 26, width: 'auto', display: 'block' }}
            />
            <Typography sx={{ fontWeight: 900, letterSpacing: 2.4, fontSize: '1.05rem', lineHeight: 1 }}>
              {brand.name}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.78rem',
              letterSpacing: 0.25,
              lineHeight: 1.4,
              color: panelMuted,
              maxWidth: '92%'
            }}
          >
            {brand.expansion}
          </Typography>
        </Box>

        <List sx={{ px: 1.35, py: 1.35 }} aria-label='SOCR boards'>
          {brandNavItems.map((board) => {
            const active = isBoardActive(pathname, board)
            const boardAccent = resolveBoardAccent(theme, board.id)
            const Icon = BOARD_ICONS[board.id] || DashboardIcon

            return (
              <ListItemButton
                key={board.id}
                selected={active}
                onClick={() => {
                  setOpen(false)
                  if (!active) navigate(board.path)
                }}
                sx={{
                  borderRadius: 1.75,
                  mb: 0.65,
                  py: 1.05,
                  px: 1.15,
                  gap: 1.15,
                  border: `1px solid ${active ? alpha(boardAccent, 0.55) : alpha('#fff', 0.08)}`,
                  backgroundColor: active ? alpha(boardAccent, 0.18) : alpha(panelSurface, 0.55),
                  backgroundImage: active
                    ? `linear-gradient(115deg, ${alpha(boardAccent, 0.28)} 0%, ${alpha(boardAccent, 0.06)} 55%, transparent 100%)`
                    : 'none',
                  boxShadow: active
                    ? `inset 3px 0 0 ${boardAccent}, 0 8px 18px ${alpha(boardAccent, 0.12)}`
                    : 'none',
                  transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease',
                  '&.Mui-selected': {
                    backgroundColor: alpha(boardAccent, 0.18)
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: alpha(boardAccent, 0.24)
                  },
                  '&:hover': {
                    borderColor: alpha(boardAccent, 0.4),
                    backgroundColor: alpha(boardAccent, active ? 0.24 : 0.12),
                    transform: 'translateX(2px)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.25,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    color: active ? '#fff' : alpha(boardAccent, 0.95),
                    backgroundColor: active ? boardAccent : alpha(boardAccent, 0.16),
                    border: `1px solid ${alpha(boardAccent, active ? 0.7 : 0.28)}`,
                    boxShadow: active ? `0 4px 12px ${alpha(boardAccent, 0.35)}` : 'none'
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </Box>

                <ListItemText
                  primary={board.label}
                  primaryTypographyProps={{
                    fontWeight: active ? 800 : 700,
                    fontSize: '0.92rem',
                    color: panelInk
                  }}
                  sx={{ my: 0, minWidth: 0 }}
                />

                {active
                  ? (
                    <Chip
                      label='Active'
                      size='small'
                      sx={{
                        height: 22,
                        flexShrink: 0,
                        fontWeight: 800,
                        fontSize: '0.65rem',
                        letterSpacing: 0.35,
                        textTransform: 'uppercase',
                        color: '#fff',
                        backgroundColor: alpha(boardAccent, 0.92),
                        border: `1px solid ${alpha('#fff', 0.18)}`,
                        '& .MuiChip-label': { px: 0.85 }
                      }}
                    />
                    )
                  : null}
              </ListItemButton>
            )
          })}
        </List>
      </Drawer>
    </>
  )
}

export { SocrNavDrawer, isBoardActive }
